import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import VehicleSelector from "./VehicleSelector";
import QuantityControl from "./QuantityControl";
import { getPickupMinLeadError } from "./DateSelectModal";
import { useVehicles } from "../../hooks/VehicleContext";
import {
  getBookingQuantityLimits,
  clampQuantities,
} from "../../utils/vehicleBookingLimits";
import useGooglePlaces, {
  getPlaceFullAddress,
  placeFromStored,
  placeToStored,
} from "../../hooks/useGooglePlaces";
import PlaceSuggestionLabel from "./PlaceSuggestionLabel";
import axios from "axios";
import { toast } from "../../utils/toast";
import {
  validatePickupServiceArea,
  isOutsideServiceAreaMessage,
  hasResolvedPlaceCoords,
} from "../../utils/validatePickupServiceArea";
import {
  getStopRouteValidationError,
  validateStopsOnRoute,
} from "../../utils/validateStopOnRoute";
import {
  BOOKING_DRAFT_STORAGE_KEY,
  writeSessionBookingDraft,
} from "../../utils/bookingStorageKeys";
import { sanitizeOneWayBookingForCheckout } from "../../utils/checkoutBookingResolve";
import {
  CHECK_FARE_TOAST_ID,
  flattenLaravelErrors,
  resolveApiPayloadMessage,
  resolveAxiosErrorMessage,
} from "../../utils/apiValidationErrors";
import {
  validateOutstationRoundTripReturnWindow,
  getReturnPickupWithin12hConfig,
  getDefaultReturnPickupDisplay,
} from "../../utils/outstationReturnWindow";

/** Round-trip return leg uses the same stops in reverse order. */
function mapStopsWithCoords(stops = [], { reverse = false } = {}) {
  const mapped = (Array.isArray(stops) ? stops : [])
    .filter((s) => getPlaceFullAddress(s))
    .map((s) => placeToStored(s));
  return reverse ? [...mapped].reverse() : mapped;
}

function mapStopsLatLng(stops = [], { reverse = false } = {}) {
  return mapStopsWithCoords(stops, { reverse }).map(({ lat, lng }) => ({
    lat,
    lng,
  }));
}
const RIDE_OUTSTATION = 3;

const toApiDateTimeParts = (value) => {
  if (!value) return { date: null, time: null };
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return { date: null, time: null };
  const date = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
    dt.getDate(),
  ).padStart(2, "0")}`;
  const time = `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(
    2,
    "0",
  )}`;
  return { date, time };
};

export default function OutstationTrip({
  onOpenDatePicker,
  setBookingData,
  initialData,
  /** Latest Check Fare payload from BookingPage — preview must merge this, not only stale editData. */
  parentBookingSnapshot = null,
  checkoutRevisionRef = 0,
  onCheckFareConfigChange,
}) {
  const [activeSubTab, setActiveSubTab] = useState("oneway");
  const [fareLoading, setFareLoading] = useState(false);
  const [owPickupServiceAreaError, setOwPickupServiceAreaError] =
    useState("");
  const [rtPickupServiceAreaError, setRtPickupServiceAreaError] =
    useState("");
  /** API validation / check-fare errors — shown on this form (not only toast). */
  const [fareApiError, setFareApiError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [sameDv, setSameDv] = useState(false);
  const owPickupInputRef = useRef(null);
  const owDropoffInputRef = useRef(null);
  const owScheduleInputRef = useRef(null);
  const rtPickupInputRef = useRef(null);
  const rtDropoffInputRef = useRef(null);
  const rtPickupTimeInputRef = useRef(null);
  const rtDropoffTimeInputRef = useRef(null);

  // One Way state
  const [owVehicle, setOwVehicle] = useState("");
  const [owPassengers, setOwPassengers] = useState(1);
  const [owSuitcase, setOwSuitcase] = useState(0);
  const [owPickup, setOwPickup] = useState({
    address: "",
    lat: null,
    lng: null,
  });
  const [owStops, setOwStops] = useState([]);
  const [owDropoff, setOwDropoff] = useState({
    address: "",
    lat: null,
    lng: null,
  });
  const [owSchedule, setOwSchedule] = useState("");

  // Round Trip state
  const [rtVehicle, setRtVehicle] = useState("");
  const [rtPassengers, setRtPassengers] = useState(1);
  const [rtSuitcase, setRtSuitcase] = useState(0);
  const [rtPickup, setRtPickup] = useState({
    address: "",
    lat: null,
    lng: null,
  });
  const [rtStops, setRtStops] = useState([]);
  const [rtDropoff, setRtDropoff] = useState({
    address: "",
    lat: null,
    lng: null,
  });
  const [rtPickupTime, setRtPickupTime] = useState("");
  const [rtDropoffTime, setRtDropoffTime] = useState("");

  useEffect(() => {
    if (!rtPickupTime) return;
    setRtDropoffTime((prev) => {
      if (!prev) return prev;
      const validation = validateOutstationRoundTripReturnWindow(
        rtPickupTime,
        prev,
      );
      if (!validation.ok) {
        toast.info(
          "Return pick-up time was cleared — it must be within 12 hours of pick-up.",
        );
        return "";
      }
      return prev;
    });
  }, [rtPickupTime]);

  /** Switching Round trip → One way: drop stale booking_type 2 + return leg so checkout never shows return block. */
  const subTabSwitchCleanupRef = useRef(false);
  useEffect(() => {
    if (!subTabSwitchCleanupRef.current) {
      subTabSwitchCleanupRef.current = true;
      return;
    }
    if (activeSubTab !== "oneway") return;
    setBookingData((prev) => {
      if (!prev || Number(prev.ride_type) !== RIDE_OUTSTATION) return prev;
      if (Number(prev.booking_type) !== 2) return prev;
      const next = sanitizeOneWayBookingForCheckout({
        ...prev,
        booking_type: 1,
      });
      return { ...next, fare_details: null };
    });
  }, [activeSubTab, setBookingData]);

  const { vehicles } = useVehicles();
  const owSelectedVehicle = useMemo(
    () => vehicles.find((v) => Number(v.id) === Number(owVehicle)),
    [vehicles, owVehicle],
  );
  const rtSelectedVehicle = useMemo(
    () => vehicles.find((v) => Number(v.id) === Number(rtVehicle)),
    [vehicles, rtVehicle],
  );
  const owLimits = useMemo(
    () => getBookingQuantityLimits(owSelectedVehicle),
    [owSelectedVehicle],
  );
  const rtLimits = useMemo(
    () => getBookingQuantityLimits(rtSelectedVehicle),
    [rtSelectedVehicle],
  );

  const hydratedEditRef = useRef(null);

  useEffect(() => {
    if (!initialData || initialData.ride_type !== RIDE_OUTSTATION) {
      hydratedEditRef.current = null;
      return;
    }
    if (hydratedEditRef.current === initialData) return;
    hydratedEditRef.current = initialData;

    setSameDv(Boolean(initialData.same_dv_multiday));
    if (initialData.booking_type === 2) {
        setActiveSubTab("roundtrip");
        setRtPassengers(initialData.no_of_passengers || 1);
        const rsc = Number(initialData.suitcase);
        setRtSuitcase(Number.isFinite(rsc) ? rsc : 0);
        setRtPickup(
          placeFromStored({
            title: initialData.pickup_title,
            address: initialData.pickup || "",
            lat: initialData.pickup_lat || null,
            lng: initialData.pickup_lng || null,
          }),
        );
        setRtDropoff(
          placeFromStored({
            title: initialData.dropoff_title,
            address: initialData.dropoff || "",
            lat: initialData.dropoff_lat || null,
            lng: initialData.dropoff_lng || null,
          }),
        );
        setRtPickupTime(initialData.schedule || "");
        setRtDropoffTime(initialData.return_schedule || "");
        if (initialData.stops && initialData.stops.length > 0)
          setRtStops(
            initialData.stops.map((s) =>
              placeFromStored({
                title: s.title,
                address: s.address,
                lat: s.lat,
                lng: s.lng,
              }),
            ),
          );
        else setRtStops([]);

        const vt =
          initialData.rides?.[0]?.vehicle_type ??
          initialData.rides?.[0]?.vehicle_type_id ??
          initialData.vehicle_type ??
          initialData.vehicle_type_id;
        if (vt) setRtVehicle(vt);
      } else {
        setActiveSubTab("oneway");
        setOwPassengers(initialData.no_of_passengers || 1);
        const osc = Number(initialData.suitcase);
        setOwSuitcase(Number.isFinite(osc) ? osc : 0);
        setOwPickup(
          placeFromStored({
            title: initialData.pickup_title,
            address: initialData.pickup || "",
            lat: initialData.pickup_lat || null,
            lng: initialData.pickup_lng || null,
          }),
        );
        setOwDropoff(
          placeFromStored({
            title: initialData.dropoff_title,
            address: initialData.dropoff || "",
            lat: initialData.dropoff_lat || null,
            lng: initialData.dropoff_lng || null,
          }),
        );
        setOwSchedule(initialData.schedule || "");
        if (initialData.stops && initialData.stops.length > 0)
          setOwStops(
            initialData.stops.map((s) =>
              placeFromStored({
                title: s.title,
                address: s.address,
                lat: s.lat,
                lng: s.lng,
              }),
            ),
          );
        else setOwStops([]);

        const vt =
          initialData.rides?.[0]?.vehicle_type ??
          initialData.rides?.[0]?.vehicle_type_id ??
          initialData.vehicle_type ??
          initialData.vehicle_type_id;
        if (vt) setOwVehicle(vt);
    }
  }, [initialData]);

  const [activeField, setActiveField] = useState(null);
  const { suggestions, fetchSuggestions, getPlaceDetails, setSuggestions } =
    useGooglePlaces();

  useEffect(() => {
    const { passengers, suitcase } = clampQuantities({
      passengers: owPassengers,
      suitcase: owSuitcase,
      maxPassengers: owLimits.maxPassengers,
      maxSuitcase: owLimits.maxSuitcase,
    });
    if (passengers !== owPassengers) setOwPassengers(passengers);
    if (suitcase !== owSuitcase) setOwSuitcase(suitcase);
  }, [
    owLimits.maxPassengers,
    owLimits.maxSuitcase,
    owPassengers,
    owSuitcase,
  ]);

  useEffect(() => {
    const { passengers, suitcase } = clampQuantities({
      passengers: rtPassengers,
      suitcase: rtSuitcase,
      maxPassengers: rtLimits.maxPassengers,
      maxSuitcase: rtLimits.maxSuitcase,
    });
    if (passengers !== rtPassengers) setRtPassengers(passengers);
    if (suitcase !== rtSuitcase) setRtSuitcase(suitcase);
  }, [
    rtLimits.maxPassengers,
    rtLimits.maxSuitcase,
    rtPassengers,
    rtSuitcase,
  ]);

  const handlePlaceSelect = async (place, field) => {
    try {
      setFareApiError("");
      const details = await getPlaceDetails(place.place_id, place);
      const applyStop = async (pickup, drop, stops, setStops, idx) => {
        const routeErr = await getStopRouteValidationError(pickup, drop, details);
        if (routeErr) {
          toast.error(routeErr);
          return;
        }
        const s = [...stops];
        s[idx] = details;
        setStops(s);
      };
      if (field === "owPickup") {
        setOwPickup(details);
        const v = await validatePickupServiceArea(details.lat, details.lng);
        setOwPickupServiceAreaError(v.ok ? "" : v.message || "");
      } else if (field === "owDropoff") setOwDropoff(details);
      else if (field === "rtPickup") {
        setRtPickup(details);
        const v = await validatePickupServiceArea(details.lat, details.lng);
        setRtPickupServiceAreaError(v.ok ? "" : v.message || "");
      } else if (field === "rtDropoff") setRtDropoff(details);
      else if (field.startsWith("owStop-")) {
        const idx = parseInt(field.split("-")[1]);
        await applyStop(owPickup, owDropoff, owStops, setOwStops, idx);
      } else if (field.startsWith("rtStop-")) {
        const idx = parseInt(field.split("-")[1]);
        await applyStop(rtPickup, rtDropoff, rtStops, setRtStops, idx);
      }
      setSuggestions([]);
      setActiveField(null);
    } catch (error) {
      console.error(error);
    }
  };

  // Add Stop helpers
  const addOwStop = () =>
    setOwStops([...owStops, { address: "", lat: null, lng: null }]);
  const updateOwStop = (i, v) => {
    const s = [...owStops];
    s[i] = { ...s[i], address: v, lat: null, lng: null };
    setOwStops(s);
    setActiveField(`owStop-${i}`);
    fetchSuggestions(v);
  };
  const removeOwStop = (i) => {
    setOwStops(owStops.filter((_, idx) => idx !== i));
  };

  const isOwValid =
    owPickup.address &&
    owDropoff.address &&
    owSchedule &&
    (owVehicle !== "" && owVehicle != null) &&
    hasResolvedPlaceCoords(owPickup) &&
    hasResolvedPlaceCoords(owDropoff) &&
    !owPickupServiceAreaError;

  const addRtStop = () =>
    setRtStops([...rtStops, { address: "", lat: null, lng: null }]);
  const updateRtStop = (i, v) => {
    const s = [...rtStops];
    s[i] = { ...s[i], address: v, lat: null, lng: null };
    setRtStops(s);
    setActiveField(`rtStop-${i}`);
    fetchSuggestions(v);
  };
  const removeRtStop = (i) => {
    setRtStops(rtStops.filter((_, idx) => idx !== i));
  };

  const isRtValid =
    rtPickup.address &&
    rtDropoff.address &&
    rtPickupTime &&
    rtDropoffTime &&
    (rtVehicle !== "" && rtVehicle != null) &&
    hasResolvedPlaceCoords(rtPickup) &&
    hasResolvedPlaceCoords(rtDropoff) &&
    !rtPickupServiceAreaError;

  const focusInvalidField = useCallback((fieldKey) => {
    const refs = {
      owPickup: owPickupInputRef,
      owDropoff: owDropoffInputRef,
      owSchedule: owScheduleInputRef,
      rtPickup: rtPickupInputRef,
      rtDropoff: rtDropoffInputRef,
      rtPickupTime: rtPickupTimeInputRef,
      rtDropoffTime: rtDropoffTimeInputRef,
    };
    refs[fieldKey]?.current?.focus?.();
  }, []);

  const getCheckFareValidation = useCallback((frm) => {
    const isOw = frm.activeSubTab === "oneway";
    if (isOw) {
      if (!frm.owPickup.address) {
        return {
          errors: { owPickup: "Pick-up is required." },
          firstField: "owPickup",
        };
      }
      if (!hasResolvedPlaceCoords(frm.owPickup)) {
        return {
          errors: {
            owPickup: "Select pick-up from suggestions to confirm location.",
          },
          firstField: "owPickup",
        };
      }
      if (!frm.owDropoff.address) {
        return {
          errors: { owDropoff: "Drop-off is required." },
          firstField: "owDropoff",
        };
      }
      if (!hasResolvedPlaceCoords(frm.owDropoff)) {
        return {
          errors: {
            owDropoff: "Select drop-off from suggestions to confirm location.",
          },
          firstField: "owDropoff",
        };
      }
      if (!frm.owSchedule) {
        return {
          errors: { owSchedule: "Pick-up time is required." },
          firstField: "owSchedule",
        };
      }
      const owLeadErr = getPickupMinLeadError(frm.owSchedule);
      if (owLeadErr) {
        return {
          errors: { owSchedule: owLeadErr },
          firstField: "owSchedule",
        };
      }
      if (frm.owPickupServiceAreaError) {
        return {
          errors: { owPickup: frm.owPickupServiceAreaError },
          firstField: "owPickup",
        };
      }
      return { errors: {}, firstField: null };
    }

    if (!frm.rtPickup.address) {
      return {
        errors: { rtPickup: "Pick-up is required." },
        firstField: "rtPickup",
      };
    }
    if (!hasResolvedPlaceCoords(frm.rtPickup)) {
      return {
        errors: {
          rtPickup: "Select pick-up from suggestions to confirm location.",
        },
        firstField: "rtPickup",
      };
    }
    if (!frm.rtDropoff.address) {
      return {
        errors: { rtDropoff: "Drop-off is required." },
        firstField: "rtDropoff",
      };
    }
    if (!hasResolvedPlaceCoords(frm.rtDropoff)) {
      return {
        errors: {
          rtDropoff: "Select drop-off from suggestions to confirm location.",
        },
        firstField: "rtDropoff",
      };
    }
    if (!frm.rtPickupTime) {
      return {
        errors: { rtPickupTime: "Pick-up time is required." },
        firstField: "rtPickupTime",
      };
    }
    const rtLeadErr = getPickupMinLeadError(frm.rtPickupTime);
    if (rtLeadErr) {
      return {
        errors: { rtPickupTime: rtLeadErr },
        firstField: "rtPickupTime",
      };
    }
    if (!frm.rtDropoffTime) {
      return {
        errors: { rtDropoffTime: "Return pick-up time is required." },
        firstField: "rtDropoffTime",
      };
    }
    if (frm.rtPickupServiceAreaError) {
      return {
        errors: { rtPickup: frm.rtPickupServiceAreaError },
        firstField: "rtPickup",
      };
    }
    return { errors: {}, firstField: null };
  }, []);

  /** Checkout edit → Back: parent bookingData is null until Check Fare; sync form → sessionStorage. */
  const buildCheckoutPreviewFromForm = useCallback(() => {
    const base = parentBookingSnapshot ?? initialData;
    if (!base || base.ride_type !== RIDE_OUTSTATION) return null;
    const isOw = activeSubTab === "oneway";
    const isValid = isOw ? isOwValid : isRtValid;
    /** Allow persisting draft for Back → checkout even if coords not "resolved" yet (only for display draft). */
    const hasLooseDraftInputs = isOw
      ? !!(
          owSchedule &&
          owVehicle !== "" &&
          owVehicle != null &&
          owPickup.address &&
          owDropoff.address &&
          !owPickupServiceAreaError
        )
      : !!(
          rtPickupTime &&
          rtDropoffTime &&
          rtVehicle !== "" &&
          rtVehicle != null &&
          rtPickup.address &&
          rtDropoff.address &&
          !rtPickupServiceAreaError
        );
    if (!isValid && !hasLooseDraftInputs) return null;

    const r0 = base.rides?.[0] || {};
    const pickupParts = toApiDateTimeParts(isOw ? owSchedule : rtPickupTime);
    const returnParts = toApiDateTimeParts(rtDropoffTime);
    const pax = isOw ? owPassengers : rtPassengers;
    const bags = isOw ? owSuitcase : rtSuitcase;
    const vt =
      (isOw ? owVehicle : rtVehicle) ||
      base.rides?.[0]?.vehicle_type ||
      "";

    const ridePayload = {
      vehicle_type: vt,
      pickup_lat: isOw
        ? (owPickup.lat ?? r0.pickup_lat ?? base.pickup_lat)
        : (rtPickup.lat ?? r0.pickup_lat ?? base.pickup_lat),
      pickup_lng: isOw
        ? (owPickup.lng ?? r0.pickup_lng ?? base.pickup_lng)
        : (rtPickup.lng ?? r0.pickup_lng ?? base.pickup_lng),
      drop_lat: isOw
        ? (owDropoff.lat ?? r0.drop_lat ?? base.dropoff_lat)
        : (rtDropoff.lat ?? r0.drop_lat ?? base.dropoff_lat),
      drop_lng: isOw
        ? (owDropoff.lng ?? r0.drop_lng ?? base.dropoff_lng)
        : (rtDropoff.lng ?? r0.drop_lng ?? base.dropoff_lng),
      pickup_date: pickupParts.date,
      pickup_time: pickupParts.time,
      no_of_passengers: pax,
      suitcase: bags,
      stops: mapStopsLatLng(isOw ? owStops : rtStops),
    };

    if (!isOw) {
      ridePayload.return_date = returnParts.date;
      ridePayload.return_time = returnParts.time;
      ridePayload.return_pickup_lat =
        rtDropoff.lat ?? r0.return_pickup_lat ?? base.return_pickup_lat;
      ridePayload.return_pickup_lng =
        rtDropoff.lng ?? r0.return_pickup_lng ?? base.return_pickup_lng;
      ridePayload.return_drop_lat =
        rtPickup.lat ?? r0.return_drop_lat ?? base.return_dropoff_lat;
      ridePayload.return_drop_lng =
        rtPickup.lng ?? r0.return_drop_lng ?? base.return_dropoff_lng;
      ridePayload.return_stops = mapStopsLatLng(rtStops, { reverse: true });
    }

    const payload = {
      ride_type: RIDE_OUTSTATION,
      booking_type: isOw ? 1 : 2,
      same_dv_multiday: sameDv ? 1 : 0,
      no_of_passengers: pax,
      suitcase: bags,
      rides: [ridePayload],
    };

    const onwardStops = mapStopsWithCoords(isOw ? owStops : rtStops);
    const returnStops = isOw
      ? []
      : mapStopsWithCoords(rtStops, { reverse: true });

    return {
      ...base,
      ...payload,
      pickup: isOw
        ? getPlaceFullAddress(owPickup)
        : getPlaceFullAddress(rtPickup),
      pickup_title: isOw ? owPickup.address : rtPickup.address,
      pickup_lat: payload.rides[0].pickup_lat,
      pickup_lng: payload.rides[0].pickup_lng,
      dropoff: isOw
        ? getPlaceFullAddress(owDropoff)
        : getPlaceFullAddress(rtDropoff),
      dropoff_title: isOw ? owDropoff.address : rtDropoff.address,
      dropoff_lat: payload.rides[0].drop_lat,
      dropoff_lng: payload.rides[0].drop_lng,
      stops: onwardStops,
      return_stops: returnStops,
      schedule: isOw ? owSchedule : rtPickupTime,
      return_schedule: isOw ? null : rtDropoffTime,
      return_pickup: isOw ? null : getPlaceFullAddress(rtDropoff),
      return_pickup_title: isOw ? null : rtDropoff.address,
      return_pickup_lat: isOw ? null : rtDropoff.lat,
      return_pickup_lng: isOw ? null : rtDropoff.lng,
      return_dropoff: isOw ? null : getPlaceFullAddress(rtPickup),
      return_dropoff_title: isOw ? null : rtPickup.address,
      return_dropoff_lat: isOw ? null : rtPickup.lat,
      return_dropoff_lng: isOw ? null : rtPickup.lng,
      no_of_passengers: pax,
      suitcase: bags,
      fare_details: base.fare_details,
      rides: payload.rides,
    };
  }, [
    initialData,
    parentBookingSnapshot,
    activeSubTab,
    isOwValid,
    isRtValid,
    owPickupServiceAreaError,
    rtPickupServiceAreaError,
    owSchedule,
    rtPickupTime,
    rtDropoffTime,
    owPassengers,
    rtPassengers,
    owSuitcase,
    rtSuitcase,
    owVehicle,
    rtVehicle,
    owPickup,
    rtPickup,
    owDropoff,
    rtDropoff,
    owStops,
    rtStops,
    sameDv,
  ]);

  /** Persist before paint so browser Back → checkout reads fresh draft (useEffect ran too late). */
  useLayoutEffect(() => {
    if (!initialData || initialData.ride_type !== RIDE_OUTSTATION) return;
    const preview = buildCheckoutPreviewFromForm();
    if (!preview) return;
    try {
      const ts = Math.max(Date.now(), (checkoutRevisionRef || 0) + 1);
      sessionStorage.setItem(
        BOOKING_DRAFT_STORAGE_KEY,
        JSON.stringify({ ts, data: preview }),
      );
    } catch (_) {
      /* ignore */
    }
  }, [initialData, checkoutRevisionRef, buildCheckoutPreviewFromForm]);

  /** Always read latest form state on click — parent keeps a stable onClick; without this, first
   * Check Fare after selecting a suggestion could use stale owPickup (null lat) from an old closure. */
  const checkFareStateRef = useRef(null);
  checkFareStateRef.current = {
    activeSubTab,
    isOwValid,
    isRtValid,
    owSchedule,
    rtPickupTime,
    rtDropoffTime,
    owPassengers,
    rtPassengers,
    owSuitcase,
    rtSuitcase,
    owVehicle,
    rtVehicle,
    owPickup,
    owDropoff,
    rtPickup,
    rtDropoff,
    owStops,
    rtStops,
    owPickupServiceAreaError,
    rtPickupServiceAreaError,
    sameDv,
    parentBookingSnapshot,
    initialData,
  };

  const handleCheckFare = useCallback(async () => {
    const frm = checkFareStateRef.current;

    const isOwTab = frm.activeSubTab === "oneway";
    if (isOwTab) {
      const stopErr = await validateStopsOnRoute(
        frm.owPickup,
        frm.owDropoff,
        frm.owStops,
      );
      if (stopErr) {
        toast.error(stopErr.message);
        focusInvalidField("owPickup");
        return;
      }
    } else {
      let stopErr = await validateStopsOnRoute(
        frm.rtPickup,
        frm.rtDropoff,
        frm.rtStops,
      );
      if (stopErr) {
        toast.error(stopErr.message);
        focusInvalidField("rtPickup");
        return;
      }
      stopErr = await validateStopsOnRoute(
        frm.rtDropoff,
        frm.rtPickup,
        mapStopsWithCoords(frm.rtStops, { reverse: true }),
      );
      if (stopErr) {
        toast.error(stopErr.message);
        focusInvalidField("rtDropoff");
        return;
      }
    }

    const { errors, firstField } = getCheckFareValidation(frm);
    if (firstField) {
      setFormErrors(errors);
      focusInvalidField(firstField);
      return;
    }

    setFormErrors({});

    const isOw = frm.activeSubTab === "oneway";
    if (!isOw) {
      const validation = validateOutstationRoundTripReturnWindow(
        frm.rtPickupTime,
        frm.rtDropoffTime,
      );
      if (!validation.ok) {
        toast.error(validation.message);
        return;
      }
    }

    setFareLoading(true);
    setFareApiError("");
    toast.dismiss(CHECK_FARE_TOAST_ID);
    try {
        const pickupParts = toApiDateTimeParts(
          isOw ? frm.owSchedule : frm.rtPickupTime,
        );
        const returnParts = toApiDateTimeParts(frm.rtDropoffTime);

        const pax = isOw ? frm.owPassengers : frm.rtPassengers;
        const bags = isOw ? frm.owSuitcase : frm.rtSuitcase;

        const vtRaw = isOw ? frm.owVehicle : frm.rtVehicle;
        const vehicleType =
          (vtRaw !== "" && vtRaw != null && vtRaw !== undefined
            ? vtRaw
            : null) ??
          frm.parentBookingSnapshot?.rides?.[0]?.vehicle_type ??
          frm.initialData?.rides?.[0]?.vehicle_type;
        if (
          vehicleType === null ||
          vehicleType === undefined ||
          vehicleType === ""
        ) {
          toast.error("Please select a preferred vehicle.");
          return;
        }

        const ridePayload = {
          vehicle_type: vehicleType,
          pickup_lat: isOw ? frm.owPickup.lat : frm.rtPickup.lat,
          pickup_lng: isOw ? frm.owPickup.lng : frm.rtPickup.lng,
          drop_lat: isOw ? frm.owDropoff.lat : frm.rtDropoff.lat,
          drop_lng: isOw ? frm.owDropoff.lng : frm.rtDropoff.lng,
          pickup_date: pickupParts.date,
          pickup_time: pickupParts.time,
          no_of_passengers: pax,
          suitcase: bags,
          stops: mapStopsLatLng(isOw ? frm.owStops : frm.rtStops),
        };

        // Add return coordinates for round trip fare calculation
        if (!isOw) {
          ridePayload.return_date = returnParts.date;
          ridePayload.return_time = returnParts.time;
          ridePayload.return_pickup_lat = frm.rtDropoff.lat;
          ridePayload.return_pickup_lng = frm.rtDropoff.lng;
          ridePayload.return_drop_lat = frm.rtPickup.lat;
          ridePayload.return_drop_lng = frm.rtPickup.lng;
          ridePayload.return_stops = mapStopsLatLng(frm.rtStops, {
            reverse: true,
          });
        }

        const payload = {
          ride_type: RIDE_OUTSTATION, // RIDE_OUTSTATION
          booking_type: isOw ? 1 : 2,
          same_dv_multiday: frm.sameDv ? 1 : 0,
          no_of_passengers: pax,
          suitcase: bags,
          rides: [ridePayload],
        };

        const response = await axios.post(
          `${process.env.REACT_APP_APP_DOMAIN}/api/customer/check-fare`,
          payload,
        );

        if (response.data.status) {
          setFareApiError("");
          toast.dismiss(CHECK_FARE_TOAST_ID);
          setOwPickupServiceAreaError("");
          setRtPickupServiceAreaError("");
          const next = {
            ...payload,
            pickup: isOw
              ? getPlaceFullAddress(frm.owPickup)
              : getPlaceFullAddress(frm.rtPickup),
            pickup_title: isOw
              ? frm.owPickup.address
              : frm.rtPickup.address,
            pickup_lat: payload.rides[0].pickup_lat,
            pickup_lng: payload.rides[0].pickup_lng,
            dropoff: isOw
              ? getPlaceFullAddress(frm.owDropoff)
              : getPlaceFullAddress(frm.rtDropoff),
            dropoff_title: isOw
              ? frm.owDropoff.address
              : frm.rtDropoff.address,
            dropoff_lat: payload.rides[0].drop_lat,
            dropoff_lng: payload.rides[0].drop_lng,
            stops: mapStopsWithCoords(isOw ? frm.owStops : frm.rtStops),
            return_stops: isOw
              ? []
              : mapStopsWithCoords(frm.rtStops, { reverse: true }),
            schedule: isOw ? frm.owSchedule : frm.rtPickupTime,
            return_schedule: isOw ? null : frm.rtDropoffTime,
            // Return locations for round trip
            return_pickup: isOw ? null : getPlaceFullAddress(frm.rtDropoff),
            return_pickup_title: isOw ? null : frm.rtDropoff.address,
            return_pickup_lat: isOw ? null : frm.rtDropoff.lat,
            return_pickup_lng: isOw ? null : frm.rtDropoff.lng,
            return_dropoff: isOw ? null : getPlaceFullAddress(frm.rtPickup),
            return_dropoff_title: isOw ? null : frm.rtPickup.address,
            return_dropoff_lat: isOw ? null : frm.rtPickup.lat,
            return_dropoff_lng: isOw ? null : frm.rtPickup.lng,
            no_of_passengers: pax,
            suitcase: bags,
            fare_details: response.data.fare_details,
            distance: response.data.fare_details?.distance_km ?? null,
            return_distance: response.data.fare_details?.return_distance_km ?? null,
          };
          setBookingData(next);
          writeSessionBookingDraft(next);

          // Show modal
          setTimeout(() => {
            const modalElement = document.getElementById("updatePolicy-Modal");
            if (modalElement && window.bootstrap) {
              const modal =
                window.bootstrap.Modal.getOrCreateInstance(modalElement);
              modal.show();
            }
          }, 200);
        } else {
          const data = response.data;
          const inline = flattenLaravelErrors(data?.errors).join("\n");
          const msg =
            resolveApiPayloadMessage(data) ||
            inline.replace(/\n/g, " ").trim() ||
            "Something went wrong";
          setFareApiError(inline || msg);
          const errKeys = Object.keys(data?.errors || {}).join(" ");
          if (/pickup_lat|pickup_lng/i.test(errKeys)) {
            const hint =
              "Pick-up coordinates are required. Select the address from the suggestions list.";
            if (isOw) setOwPickupServiceAreaError(hint);
            else setRtPickupServiceAreaError(hint);
          }
          if (isOutsideServiceAreaMessage(msg)) {
            if (isOw) setOwPickupServiceAreaError(msg);
            else setRtPickupServiceAreaError(msg);
          }
          toast.error(msg, { toastId: CHECK_FARE_TOAST_ID });
        }
      } catch (error) {
        console.error("Fare check error:", error);
        const data = error.response?.data;
        const inline = flattenLaravelErrors(data?.errors).join("\n");
        const msg =
          resolveAxiosErrorMessage(error) ||
          inline.replace(/\n/g, " ").trim() ||
          "Failed to check fare. Please try again.";
        setFareApiError(inline || msg);
        const errKeys = Object.keys(data?.errors || {}).join(" ");
        if (/pickup_lat|pickup_lng/i.test(errKeys)) {
          const hint =
            "Pick-up coordinates are required. Select the address from the suggestions list.";
          if (isOw) setOwPickupServiceAreaError(hint);
          else setRtPickupServiceAreaError(hint);
        }
        if (isOutsideServiceAreaMessage(msg)) {
          if (isOw) setOwPickupServiceAreaError(msg);
          else setRtPickupServiceAreaError(msg);
        }
        toast.error(msg, { toastId: CHECK_FARE_TOAST_ID });
      } finally {
        setFareLoading(false);
      }
  }, [focusInvalidField, getCheckFareValidation, setBookingData]);

  // Expose check-fare button state to parent (BookingPage)
  useEffect(() => {
    if (!onCheckFareConfigChange) return;
    onCheckFareConfigChange({
      onClick: handleCheckFare,
      disabled: fareLoading,
      label: fareLoading ? "Calculating..." : "Check Fare",
    });
  }, [
    onCheckFareConfigChange,
    fareLoading,
    handleCheckFare,
  ]);

  return (
    <div
      className="tab-pane fade active show"
      id="nav-contact"
      role="tabpanel"
      aria-labelledby="nav-contact-tab"
    >
      <div className="second_tabs">
        <h2>Outstation</h2>
        {fareApiError ? (
          <div
            className="alert alert-danger my-2"
            role="alert"
            style={{ whiteSpace: "pre-line" }}
          >
            {fareApiError}
          </div>
        ) : null}
        <nav className="nav_second">
          <div className="nav nav-tabs" id="nav-tab3" role="tablist">
            <button
              className={`nav-link${activeSubTab === "oneway" ? " active" : ""}`}
              type="button"
              role="tab"
              onClick={() => setActiveSubTab("oneway")}
            >
              One Way
            </button>
            <button
              className={`nav-link${activeSubTab === "roundtrip" ? " active" : ""}`}
              type="button"
              role="tab"
              onClick={() => setActiveSubTab("roundtrip")}
            >
              Round Trip
            </button>
          </div>
        </nav>
      </div>

      {/* ---- One Way ---- */}
      <div style={{ display: activeSubTab === "oneway" ? "block" : "none" }}>
        <div className="transport_colMain">
          <VehicleSelector
            selected={owVehicle}
            onSelect={setOwVehicle}
            disableAutoSelect={Boolean(initialData)}
          />
          <div className="form_col">
            <div className="form_title">
              <h3>Location </h3>
              <a
                href="#!"
                className="info_modal"
                onClick={(e) => {
                  e.preventDefault();
                  addOwStop();
                }}
              >
                + Add Stop
              </a>
            </div>
            <div className="input_col">
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/location_on.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className={`form-control${formErrors.owPickup ? " is-invalid" : ""}`}
                    placeholder="Pick-up"
                    value={owPickup.address}
                    ref={owPickupInputRef}
                    onChange={(e) => {
                      if (formErrors.owPickup) {
                        setFormErrors((prev) => ({ ...prev, owPickup: "" }));
                      }
                      setOwPickup({
                        address: e.target.value,
                        lat: null,
                        lng: null,
                      });
                      setFareApiError("");
                      setOwPickupServiceAreaError("");
                      setActiveField("owPickup");
                      fetchSuggestions(e.target.value);
                    }}
                  />
                  {owPickup.address && (
                    <img
                      src="/assets/img/all-images/close-icon.png"
                      alt=""
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setOwPickup({ address: "", lat: null, lng: null });
                        setFareApiError("");
                        setOwPickupServiceAreaError("");
                      }}
                    />
                  )}
                  {activeField === "owPickup" && suggestions.length > 0 && (
                    <ul className="autocomplete-suggestions">
                      {suggestions.map((s) => (
                        <li
                          key={s.place_id}
                          className="autocomplete-suggestion-item"
                          onClick={() => handlePlaceSelect(s, "owPickup")}
                        >
                          <PlaceSuggestionLabel suggestion={s} />
                        </li>
                      ))}
                    </ul>
                  )}
                  {owPickupServiceAreaError ? (
                    <div className="text-danger small mt-1">
                      {owPickupServiceAreaError}
                    </div>
                  ) : null}
                  {formErrors.owPickup ? (
                    <div className="invalid-feedback d-block">
                      {formErrors.owPickup}
                    </div>
                  ) : null}
                </div>
              </div>
              {owStops.map((stop, index) => (
                <div className="form-group" key={index}>
                  <div className="input_image">
                    <img src="/assets/img/all-images/location_on.png" alt="" />
                  </div>
                  <div className="input_box">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Add Stop"
                      value={stop.address}
                      onChange={(e) => updateOwStop(index, e.target.value)}
                    />
                    <img
                      src="/assets/img/all-images/close-icon.png"
                      alt=""
                      style={{ cursor: "pointer" }}
                      onClick={() => removeOwStop(index)}
                    />
                    {activeField === `owStop-${index}` &&
                      suggestions.length > 0 && (
                        <ul className="autocomplete-suggestions">
                          {suggestions.map((s) => (
                            <li
                              key={s.place_id}
                              className="autocomplete-suggestion-item"
                              onClick={() =>
                                handlePlaceSelect(s, `owStop-${index}`)
                              }
                            >
                              <PlaceSuggestionLabel suggestion={s} />
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                </div>
              ))}
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/location_on.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className={`form-control${formErrors.owDropoff ? " is-invalid" : ""}`}
                    placeholder="Drop-off"
                    value={owDropoff.address}
                    ref={owDropoffInputRef}
                    onChange={(e) => {
                      if (formErrors.owDropoff) {
                        setFormErrors((prev) => ({ ...prev, owDropoff: "" }));
                      }
                      setOwDropoff({
                        address: e.target.value,
                        lat: null,
                        lng: null,
                      });
                      setFareApiError("");
                      setActiveField("owDropoff");
                      fetchSuggestions(e.target.value);
                    }}
                  />
                  {owDropoff.address && (
                    <img
                      src="/assets/img/all-images/close-icon.png"
                      alt=""
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setOwDropoff({ address: "", lat: null, lng: null });
                        setFareApiError("");
                      }}
                    />
                  )}
                  {activeField === "owDropoff" && suggestions.length > 0 && (
                    <ul className="autocomplete-suggestions">
                      {suggestions.map((s) => (
                        <li
                          key={s.place_id}
                          className="autocomplete-suggestion-item"
                          onClick={() => handlePlaceSelect(s, "owDropoff")}
                        >
                          <PlaceSuggestionLabel suggestion={s} />
                        </li>
                      ))}
                    </ul>
                  )}
                  {formErrors.owDropoff ? (
                    <div className="invalid-feedback d-block">
                      {formErrors.owDropoff}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="form_col">
            <div className="form_title">
              <h3>Schedule </h3>
            </div>
            <div className="input_col input_col2">
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/Calendar-icon.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className={`form-control${formErrors.owSchedule ? " is-invalid" : ""}`}
                    placeholder="Pick-up time"
                    value={owSchedule}
                    ref={owScheduleInputRef}
                    onChange={(e) => {
                      if (formErrors.owSchedule) {
                        setFormErrors((prev) => ({ ...prev, owSchedule: "" }));
                      }
                      setOwSchedule(e.target.value);
                    }}
                    onClick={() => onOpenDatePicker(setOwSchedule, {}, owSchedule)}
                    data-bs-toggle="modal"
                    data-bs-target="#dateSelectModal"
                    readOnly
                    style={{ cursor: "pointer" }}
                  />
                  {formErrors.owSchedule ? (
                    <div className="invalid-feedback d-block">
                      {formErrors.owSchedule}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="form_col">
            <div className="form_title">
              <h3>Guest Info</h3>
            </div>
            <div className="input_col input_col2">
              <div className="form-group form-groupQuantity">
                <QuantityControl
                  label="Passengers"
                  value={owPassengers}
                  onChange={setOwPassengers}
                  min={1}
                  max={owLimits.maxPassengers}
                />
                <QuantityControl
                  label="Suitcase"
                  value={owSuitcase}
                  onChange={setOwSuitcase}
                  min={0}
                  max={owLimits.maxSuitcase}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Round Trip ---- */}
      <div style={{ display: activeSubTab === "roundtrip" ? "block" : "none" }}>
        <div className="transport_colMain">
          <VehicleSelector
            selected={rtVehicle}
            onSelect={setRtVehicle}
            disableAutoSelect={Boolean(initialData)}
          />
          <div className="form_col">
            <div className="form_title">
              <h3>Location </h3>
              <a
                href="#!"
                className="info_modal"
                onClick={(e) => {
                  e.preventDefault();
                  addRtStop();
                }}
              >
                + Add Stop
              </a>
            </div>
            <div className="input_col">
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/location_on.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className={`form-control${formErrors.rtPickup ? " is-invalid" : ""}`}
                    placeholder="Pick-up"
                    value={rtPickup.address}
                    ref={rtPickupInputRef}
                    onChange={(e) => {
                      if (formErrors.rtPickup) {
                        setFormErrors((prev) => ({ ...prev, rtPickup: "" }));
                      }
                      setRtPickup({
                        address: e.target.value,
                        lat: null,
                        lng: null,
                      });
                      setFareApiError("");
                      setRtPickupServiceAreaError("");
                      setActiveField("rtPickup");
                      fetchSuggestions(e.target.value);
                    }}
                  />
                  {rtPickup.address && (
                    <img
                      src="/assets/img/all-images/close-icon.png"
                      alt=""
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setRtPickup({ address: "", lat: null, lng: null });
                        setFareApiError("");
                        setRtPickupServiceAreaError("");
                      }}
                    />
                  )}
                  {activeField === "rtPickup" && suggestions.length > 0 && (
                    <ul className="autocomplete-suggestions">
                      {suggestions.map((s) => (
                        <li
                          key={s.place_id}
                          className="autocomplete-suggestion-item"
                          onClick={() => handlePlaceSelect(s, "rtPickup")}
                        >
                          <PlaceSuggestionLabel suggestion={s} />
                        </li>
                      ))}
                    </ul>
                  )}
                  {rtPickupServiceAreaError ? (
                    <div className="text-danger small mt-1">
                      {rtPickupServiceAreaError}
                    </div>
                  ) : null}
                  {formErrors.rtPickup ? (
                    <div className="invalid-feedback d-block">
                      {formErrors.rtPickup}
                    </div>
                  ) : null}
                </div>
              </div>
              {rtStops.map((stop, index) => (
                <div className="form-group" key={index}>
                  <div className="input_image">
                    <img src="/assets/img/all-images/location_on.png" alt="" />
                  </div>
                  <div className="input_box">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Add Stop"
                      value={stop.address}
                      onChange={(e) => updateRtStop(index, e.target.value)}
                    />
                    <img
                      src="/assets/img/all-images/close-icon.png"
                      alt=""
                      style={{ cursor: "pointer" }}
                      onClick={() => removeRtStop(index)}
                    />
                    {activeField === `rtStop-${index}` &&
                      suggestions.length > 0 && (
                        <ul className="autocomplete-suggestions">
                          {suggestions.map((s) => (
                            <li
                              key={s.place_id}
                              className="autocomplete-suggestion-item"
                              onClick={() =>
                                handlePlaceSelect(s, `rtStop-${index}`)
                              }
                            >
                              <PlaceSuggestionLabel suggestion={s} />
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                </div>
              ))}
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/location_on.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className={`form-control${formErrors.rtDropoff ? " is-invalid" : ""}`}
                    placeholder="Drop-off"
                    value={rtDropoff.address}
                    ref={rtDropoffInputRef}
                    onChange={(e) => {
                      if (formErrors.rtDropoff) {
                        setFormErrors((prev) => ({ ...prev, rtDropoff: "" }));
                      }
                      setRtDropoff({
                        address: e.target.value,
                        lat: null,
                        lng: null,
                      });
                      setFareApiError("");
                      setActiveField("rtDropoff");
                      fetchSuggestions(e.target.value);
                    }}
                  />
                  {rtDropoff.address && (
                    <img
                      src="/assets/img/all-images/close-icon.png"
                      alt=""
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setRtDropoff({ address: "", lat: null, lng: null });
                        setFareApiError("");
                      }}
                    />
                  )}
                  {activeField === "rtDropoff" && suggestions.length > 0 && (
                    <ul className="autocomplete-suggestions">
                      {suggestions.map((s) => (
                        <li
                          key={s.place_id}
                          className="autocomplete-suggestion-item"
                          onClick={() => handlePlaceSelect(s, "rtDropoff")}
                        >
                          <PlaceSuggestionLabel suggestion={s} />
                        </li>
                      ))}
                    </ul>
                  )}
                  <span className="mt-2 d-block">
                    This will be your final drop-off point.
                  </span>
                  {formErrors.rtDropoff ? (
                    <div className="invalid-feedback d-block">
                      {formErrors.rtDropoff}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="form_col">
            <div className="form_title">
              <h3>Schedule </h3>
            </div>
            <div className="input_col input_col2">
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/Calendar-icon.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className={`form-control${formErrors.rtPickupTime ? " is-invalid" : ""}`}
                    placeholder="Pick-up time"
                    value={rtPickupTime}
                    ref={rtPickupTimeInputRef}
                    onChange={(e) => {
                      if (formErrors.rtPickupTime) {
                        setFormErrors((prev) => ({ ...prev, rtPickupTime: "" }));
                      }
                      setRtPickupTime(e.target.value);
                    }}
                    onClick={() =>
                      onOpenDatePicker(setRtPickupTime, {}, rtPickupTime)
                    }
                    data-bs-toggle="modal"
                    data-bs-target="#dateSelectModal"
                    readOnly
                    style={{ cursor: "pointer" }}
                  />
                  {formErrors.rtPickupTime ? (
                    <div className="invalid-feedback d-block">
                      {formErrors.rtPickupTime}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/Calendar-icon.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className={`form-control${formErrors.rtDropoffTime ? " is-invalid" : ""}`}
                    placeholder="Return Pick-up Time"
                    value={rtDropoffTime}
                    ref={rtDropoffTimeInputRef}
                    onChange={(e) => {
                      if (formErrors.rtDropoffTime) {
                        setFormErrors((prev) => ({ ...prev, rtDropoffTime: "" }));
                      }
                      setRtDropoffTime(e.target.value);
                    }}
                    onClick={() => {
                      if (!rtPickupTime) {
                        toast.warning("Please select pick-up time first.");
                        return;
                      }
                      onOpenDatePicker(
                        setRtDropoffTime,
                        getReturnPickupWithin12hConfig(rtPickupTime),
                        rtDropoffTime ||
                          getDefaultReturnPickupDisplay(rtPickupTime),
                      );
                    }}
                    data-bs-toggle="modal"
                    data-bs-target="#dateSelectModal"
                    readOnly
                    style={{ cursor: "pointer" }}
                  />
                  {formErrors.rtDropoffTime ? (
                    <div className="invalid-feedback d-block">
                      {formErrors.rtDropoffTime}
                    </div>
                  ) : (
                    <span className="mt-2 d-block" style={{ fontSize: 13, color: "#8a6d00", background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 8, padding: "8px 12px" }}>
                      Return pick-up must be within 12 hours of the onward pick-up time. For a later return, please make a separate booking.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="form_col">
            <div className="form_title">
              <h3>Guest Info</h3>
            </div>
            <div className="input_col input_col2">
              <div className="form-group form-groupQuantity">
                <QuantityControl
                  label="Passengers"
                  value={rtPassengers}
                  onChange={setRtPassengers}
                  min={1}
                  max={rtLimits.maxPassengers}
                />
                <QuantityControl
                  label="Suitcase"
                  value={rtSuitcase}
                  onChange={setRtSuitcase}
                  min={0}
                  max={rtLimits.maxSuitcase}
                />
              </div>
            </div>
          </div>
          {/* <div className="form-group mb-2 mx-2">
                <label className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={sameDv}
                    onChange={(e) => setSameDv(e.target.checked)}
                  />
                  <span>Keep Same Driver & Vehicle for Entire Trip</span>
                </label>
              </div> */}
        </div>
      </div>
    </div>
  );
}
